"use client";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { SlOptionsVertical } from "react-icons/sl";
import { FaLink } from "react-icons/fa";

import { useAuthContext } from "@/components/providers/AuthContextProvider";
import { BookmarkIcon } from "@/components/icons/BookmarkIcon";
import { LikeIcon } from "@/components/icons/LikeIcon";
import { getLabel } from "@/data/labels";
import { socialShareOptions } from "@/data/social";
import envConfig from "@/config/envConfig";
import { useHandleClickOutside } from "@/hooks";
import { useMessage } from "@/components/providers/MessageProvider";
import { fetchActivityStatus, postBookmark, postLike } from "@/services/commonService";
import { ArticleType } from "@/types/article";
import { getNewsDetailsUrl } from "@/utils/getNewsDetailsUrl";
import { getAppLocaleFromPathname, getLocaleFromPathname } from "@/utils/getLocaleFromPath";

interface ArticleLikeBookmarkSectionProps {
  article: ArticleType;
}

const getLocalizedSocialName = (name: string, language: string): string => {
  if (language !== "te") return name;
  
  const socialNames: { [key: string]: string } = {
    "Facebook": "ఫేస్బుక్",
    "Instagram": "ఇన్స్టాగ్రామ్",
    "YouTube": "యూట్యూబ్",
    "X": "X",
    "Whatsapp": "వాట్సాప్",
    "LinkedIn": "లింక్డ్ఇన్",
    "Email": "ఇమెయిల్",
  };
  
  return socialNames[name] || name;
};

export default function ArticleLikeBookmarkSection({ article }: ArticleLikeBookmarkSectionProps) {
  const rawId = article?.id ?? (article as { _id?: string })?._id;
  const articleId = rawId != null ? String(rawId) : undefined;
  const [likeCount, setLikeCount] = useState(article?.likes || 0);
  const [isLiked, setIsLiked] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { user, refreshAuthentication } = useAuthContext();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathName = usePathname();
  const locale = getLocaleFromPathname(pathName) || "te-IN";
  const language = getAppLocaleFromPathname(pathName, article?.language);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { showMessage } = useMessage();

  useHandleClickOutside(dropdownRef, () => setIsDropdownOpen(false));

  const { data: activityStatus } = useQuery({
    queryKey: ["fetchNewsActivityStatus", articleId],
    queryFn: () => {
      const id = articleId;
      if (!id) throw new Error("No articleId");
      return fetchActivityStatus({ contentId: id, contentType: "news" });
    },
    enabled: !!user && !!articleId,
  });

  const handleLike = async () => {
    try {
      if (!user) {
        const redirectUrl = encodeURIComponent(pathName);
        router.push(`/${locale}/login?r=${redirectUrl}`);
        return;
      }
      if (!articleId) return;

      const newLikeState = !isLiked;
      await postLike({ type: "news", id: articleId, isLiked: newLikeState });
      setIsLiked(newLikeState);
      setLikeCount(prev => (newLikeState ? prev + 1 : prev - 1));
      await queryClient.invalidateQueries({ queryKey: ["fetchNewsActivityStatus", articleId] });
    } catch (err: unknown) {
      const e = err as Error & { status?: number; message?: string };
      if (e?.status === 401) refreshAuthentication();
      else showMessage("error", e?.message || (language === "te" ? "లైక్ అప్‌డేట్ విఫలమైంది" : "Failed to update like"));
    }
  };

  const handleBookmark = async () => {
    try {
      if (!user) {
        const redirectUrl = encodeURIComponent(pathName);
        router.push(`/${locale}/login?r=${redirectUrl}`);
        return;
      }
      if (!articleId) return;

      const newBookmarkState = !isBookmarked;
      await postBookmark({ type: "news", id: articleId, isBookmarked: newBookmarkState });
      setIsBookmarked(newBookmarkState);
      await queryClient.invalidateQueries({ queryKey: ["fetchNewsActivityStatus", articleId] });
      await queryClient.invalidateQueries({ queryKey: ["fetchAllBookmarks"] });
    } catch (err: unknown) {
      const e = err as Error & { status?: number; message?: string };
      if (e?.status === 401) refreshAuthentication();
      else showMessage("error", e?.message || (language === "te" ? "బుక్మార్క్ అప్‌డేట్ విఫలమైంది" : "Failed to update bookmark"));
    }
  };

  useEffect(() => {
    if (activityStatus) {
      setIsLiked(activityStatus?.isLiked);
      setLikeCount(activityStatus?.likesCount);
      setIsBookmarked(activityStatus?.isBookmarked);
    }
  }, [activityStatus]);

  const articleUrl = getNewsDetailsUrl(article?.categoryId, article?.subCategoryId, article?.slug, locale);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(`${envConfig?.clientUrl}${articleUrl}`);
    showMessage("info", language === "te" ? "లింక్ క్లిప్‌బోర్డ్‌కు కాపీ చేయబడింది!" : "Link copied to clipboard!");
  };

  return (
    <div className="flex flex-col gap-5 items-center sm:flex-row sm:justify-between sm:items-center sm:gap-6 w-full">
      {/* Row 1: Share icons only */}
      <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4">
        {socialShareOptions?.slice(0, 4)?.map((item, index) => {
          const Icon = item?.icon;
          const shareLink = item.generateLink(`${envConfig.clientUrl}${articleUrl}`, article?.title);

          return (
            <a
              key={index}
              href={shareLink}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${item.name}`}
              className="flex shrink-0 items-center justify-center min-h-11 min-w-11 rounded-md p-2.5 text-gray-600 transition-colors dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
            >
              <Icon className="h-5 w-5 sm:h-[22px] sm:w-[22px]" />
            </a>
          );
        })}
        <div className="mx-1 h-6 w-px shrink-0 bg-gray-400 dark:bg-gray-500" />
        <div className="relative">
          <button
            type="button"
            className="flex min-h-11 min-w-11 items-center justify-center rounded-md p-2.5 text-[#585858] transition-colors dark:text-white hover:bg-black/5 dark:hover:bg-white/10"
            onClick={() => setIsDropdownOpen(true)}
            aria-label="More Options"
          >
            <SlOptionsVertical size={16} />
          </button>

          {isDropdownOpen && (
            <div
              ref={dropdownRef}
              className="absolute top-[100%] right-0 white shadow-md z-10 bg-white dark:bg-[#444] dark:text-white min-w-[180px] py-3 rounded"
            >
              <div
                className="flex items-center gap-2 cursor-pointer px-4 py-2 transition-colors hover:text-[#0055a5]"
                onClick={handleCopyLink}
              >
                <span className="flex items-center justify-center">
                  <FaLink />
                </span>
                <span className="text-sm font-medium whitespace-nowrap">{language === "te" ? "లింక్ కాపీ చేయండి" : "Copy Link"}</span>
              </div>
              {socialShareOptions?.map((item, index) => {
                const Icon = item?.icon;
                const shareLink = item.generateLink(`${envConfig.clientUrl}${articleUrl}`, article?.title);
                const localizedName = getLocalizedSocialName(item.name, language);

                return (
                  <a
                    key={index}
                    href={shareLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Share on ${item.name}`}
                    className="flex items-center gap-2 px-4 py-2 transition-colors hover:text-[#0055a5]"
                  >
                    <span className="flex items-center justify-center w-5 h-5">
                      <Icon className="w-full h-full" />
                    </span>

                    <span className="text-sm font-medium whitespace-nowrap">{localizedName}</span>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {/* Row 2: Bookmark and Like on same line */}
      <div className="flex shrink-0 flex-wrap items-center justify-center gap-6 sm:gap-8">
        <button
          type="button"
          className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-gray-900 transition-colors dark:text-white hover:bg-black/5 dark:hover:bg-white/10"
          onClick={handleBookmark}
          aria-label="Bookmark"
        >
          <BookmarkIcon
            className="h-4 w-4 shrink-0"
            fill={isBookmarked ? "#3b82f6" : "currentColor"}
          />
          <span className="text-xs font-medium text-gray-900 dark:text-white">{getLabel(language, "BOOKMARK")}</span>
        </button>
        <button
          type="button"
          className="flex min-h-11 items-center gap-2 rounded-md px-3 py-2 text-gray-900 transition-colors dark:text-white hover:bg-black/5 dark:hover:bg-white/10"
          onClick={handleLike}
          aria-label="Like"
        >
          <LikeIcon
            className="h-4 w-4 shrink-0"
            fill={isLiked ? "#3b82f6" : "currentColor"}
          />
          <span className="text-xs font-medium text-gray-900 dark:text-white">{likeCount} {getLabel(language, "LIKES")}</span>
        </button>
        {/* <div className="flex items-center gap-1">
            <BiDislike size={16} color="#66676B" />
            <Typography variant="body2" color="#66676B" className="font-medium">
              11 Dislikes
            </Typography>
          </div> */}
      </div>
    </div>
  );
}
